import { useState, useEffect } from 'react';
import type React from 'react';
import type {
  Control,
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
  SubmitHandler,
} from 'react-hook-form';

import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import Button from 'components/partials/button/button';

import BasicInfoForm from 'containers/admin/clients/client/forms/basic-info-form/basic-info-form';
import AsignedConsultantsForm from 'containers/admin/clients/client/forms/asigned-consultants-form/asigned-consultants-form';

interface ClientFormProps {
  isFormDisabled?: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  control: Control<FieldValues>;
  onSubmit: SubmitHandler<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  errors: FieldErrors;
  clearErrors: (name?: string) => void;
}

const ClientForm = ({
  isFormDisabled,
  isSaving,
  onCancel,
  control,
  onSubmit,
  handleSubmit,
  errors,
  clearErrors,
}: ClientFormProps): React.JSX.Element => {
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);

  useEffect(() => {
    setIsSaveBtnDisabled(Object.keys(errors).length !== 0);
  }, [Object.keys(errors).length]);

  return (
    <Stack data-testid="client-form-container">
      <Stack direction="row" sx={{ width: '100%' }}>
        <Typography
          data-testid="client-form-basic-info-heading"
          variant="h3"
          sx={{ width: '50%', mr: 8 }}
        >
          Basic information
        </Typography>
        <Typography
          data-testid="client-form-consultants-heading"
          variant="h3"
          sx={{ width: '50%' }}
        >
          Assigned consultants
        </Typography>
      </Stack>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full"
        data-testid="client-form"
      >
        <Stack direction="row">
          <BasicInfoForm
            {...{ isFormDisabled, control, errors, clearErrors }}
          />
          <AsignedConsultantsForm {...{ isFormDisabled, control }} />
        </Stack>
        {!isFormDisabled && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="reserve-text"
              size="md"
              onClick={onCancel}
              data-testid="client-form-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaveBtnDisabled || isSaving}
              variant="brand-filled"
              size="md"
              data-testid="client-form-save-button"
            >
              Save client
            </Button>
          </Stack>
        )}
      </form>
    </Stack>
  );
};

export default ClientForm;
